import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { catchError, firstValueFrom, map, of, switchMap, throwError } from 'rxjs';
import { isEmpty, partition } from 'lodash-es';
import { SignType } from 'estensa-module';

import {
    CustomException,
    ExceptionFactory,
    FaultDetails,
    Importance,
} from '../../exceptions/exception.factory';
import { SissCloudLoadingComponent } from './siss-cloud-loading/siss-cloud-loading.component';
import { ISeSignResultData, SeSissCloudSignRequestData, SignPartialSuccessDetailedError } from '../../models/estensasign.model';
import { IReportWorkflowExamService } from '../../services/report-workflow-exam.service';
import { RadioSettingsService } from '../../services/settings/radio-settings.service';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { SissCloudTokenService } from './siss-cloud-sso';
import { CancellationError } from '../../confirm/confirm-dialog.service';
import { SissCloudExtraData, SissCloudInvalidTokenError, SissCloudInvalidUserError } from './siss-cloud.models';
import { ModalDialogNotificationService } from '../../services/notification.service';
import { CacheService } from '../../utils/cache.service';
import { ServerRootService } from '../../utils/routing/serverroot.service';
import { SissUploadDocumentData } from '@se/models';

export interface SissCloudOTPSmsFault extends FaultDetails {}
export class SissCloudOTPSmsException extends CustomException {
    importance = Importance.ignorable;
}
ExceptionFactory.register(SissCloudOTPSmsException, 'SissCloudOTPSmsException');

export interface UploadDocumentsResult {
    signSessionId: string;
    uploadedReportIndexes: number[];
}

@Injectable({
    providedIn: 'root',
})
export class SissCloudService extends ServerRootService {
    constructor(
        private auth: AuthenticationService,
        private cache: CacheService,
        private dialog: MatDialog,
        private http: HttpClient,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private settings: RadioSettingsService,
        private sissCloudTokenService: SissCloudTokenService,
        private translate: TranslateService,
    ) {
        super();
    }

    async prepareSign(examService: IReportWorkflowExamService, signRequestData: SeSissCloudSignRequestData, isDeferred: boolean) {
        const settings = await this.sissCloudTokenService.getSissWay3Settings();
        signRequestData.signerName = await this.auth.getAdditionalUserName(signRequestData.signerId);

        const dialogRef = this.dialog.open(SissCloudLoadingComponent, { disableClose: true });

        try {
            dialogRef.componentInstance.state = 'auth';

            const token = await this.sissCloudTokenService.getAccessToken(signRequestData.signerId);

            dialogRef.componentInstance.state = 'upload';

            // UploadDocuments ritorna gli id dei documenti che snono stati effettivamente caricati, e la sessione di
            //   firma che è stata aperta sul SISS. Usiamo quindi questi datiper aggiornare i dati di firma.
            const uploadResult = await this.uploadDocuments(examService, {
                reportIds: signRequestData.reportIdList,
                reportDescrList: signRequestData.reportErrorDetailList.map((item: any) => item.message),
                signerId: signRequestData.signerId,
                signerUsername: signRequestData.signerName,
                token,
                reportDocumentiList: signRequestData.sissDataInfoList.map((doc) => ({
                    reportInfo: doc.report[0] as any,
                    patientInfo: doc.patient,
                    obscurationInfo: doc.obscuration,
                })),
                idStruttura: settings.idStruttura,
                idSubStruttura: settings.idSubStruttura,
                codBrancaSpecialistica: settings.codBrancaSpecialistica,
                isDeferred: isDeferred ?? false,
            });

            this.updateSignRequestData(signRequestData, uploadResult);
        } catch (err) {
            if (err instanceof CancellationError) {
                // L'utente ha cancellato l'autenticazione.
                this.modalDialogNotificationService.warning(this.translate.instant('UndoOperation'));
            } else if (err instanceof SissCloudInvalidTokenError) {
                // Il token non è più valido.
                this.modalDialogNotificationService.warning(this.translate.instant(err.message));
                await this.sissCloudTokenService.logout();
            } else if (err instanceof SissCloudInvalidUserError) {
                // Il token appartiene ad un altro utente.
                this.modalDialogNotificationService.warning(this.translate.instant(err.message));
                await this.sissCloudTokenService.logout();
            } else {
                // Errore generico non generato da noi
                this.modalDialogNotificationService.error(this.translate.instant('SignReportError'));
            }

            throw err;
        } finally {
            dialogRef.close();
        }
    }

    /**
     * Una volta completato il caricamento, dobbiamo aggiornare i dati di firma con i documenti che sono stati
     *   effettivamente caricati
     */
    private updateSignRequestData(signRequestData: SeSissCloudSignRequestData, uploadResult: UploadDocumentsResult) {
        signRequestData.reportIdList = uploadResult.uploadedReportIndexes.map((i) => signRequestData.reportIdList[i]);
        signRequestData.sissDataInfoList = uploadResult.uploadedReportIndexes.map(
            (i) => signRequestData.sissDataInfoList[i],
        );
        signRequestData.reportErrorDetailList = uploadResult.uploadedReportIndexes.map(
            (i) => signRequestData.reportErrorDetailList[i],
        );

        signRequestData.extraData = {
            type: 'SissCloud',
            sessionId: uploadResult.signSessionId,
        } satisfies SissCloudExtraData;
    }

    async getAccessToken(signerId: string) {
        return this.sissCloudTokenService.getAccessToken(signerId);
    }

    async logout() {
        return this.sissCloudTokenService.logout();
    }

    async onErrorCode(errorCode: string) {
        if (errorCode === '403') {
            await this.sissCloudTokenService.logout();
        }
    }

    async requestOtpSms(signerId: string, signerCode: string, password: string, sessionId?: string) {
        let domain, userName;
        if (signerCode.includes('\\')) {
            [domain, userName] = signerCode.split('\\');
        }
        else {
            domain = '';
            userName = signerCode;
        }

        const token = await this.sissCloudTokenService.getAccessToken(signerId);

        const controller = (await this.getSignType()) === SignType.SISSCloud ? 'Siss3' : 'SissWay3';
        return firstValueFrom(
            this.http.post<void>(
                `${this.getServerRoot()}/${controller}/RequestOtpSms`,
                {
                    token,
                    domain,
                    userName,
                    password,
                    sessionId,
                },
                this.buildOptions(),
            ),
        );
    }

    async handleSignResult(signedDocDataList: ISeSignResultData[]): Promise<ISeSignResultData[]> {
        // Caso di errori con significato specifico.
        const firstErrorCodeResult = signedDocDataList.find((res) => res.serverErrorCode)?.serverErrorCode;
        if (firstErrorCodeResult)
            await this.onErrorCode(firstErrorCodeResult);

        const [signed, notSigned] = partition(signedDocDataList, (res) => res.signed);
        if (isEmpty(notSigned)) // Caso in cui tutti i documenti sono firmati.
            return signedDocDataList;


        // Caso in cui alcuni documenti sono firmati, altri no. Caso in cui sono tutti NON firmati
        throw new SignPartialSuccessDetailedError(
            signedDocDataList.length,
            signed.length,
            notSigned.map((res) => res.reportDescr ?? res.reportId),
        );
    }

    private getSignType() {
        return firstValueFrom(
            this.settings.radioSettingsAsync.pipe(map((settings) => settings.reportSettings.signType)),
        );
    }

    private async uploadDocuments(
        examService: IReportWorkflowExamService,
        uploadData: SissUploadDocumentData,
    ): Promise<UploadDocumentsResult> {
        const signType = await this.getSignType();

        const uploadOpservable =
            signType === SignType.SISSCloud
                ? examService.uploadReportToSissCloud(uploadData)
                : examService.uploadReportToSissWayCloud(uploadData);

        const signResults = await firstValueFrom(
            uploadOpservable.pipe(
                switchMap((results) => {
                    for (const result of results) {
                        // Errore speciale er cui va cancellato il token del SISS.
                        SissCloudInvalidTokenError.guard(result.serverError);
                        // Errore speciale per cui oltre a cancellare il token, bisogna mostrare la popup di logout manuale.
                        SissCloudInvalidUserError.guard(result.serverError);
                    }

                    return of(results);
                }),
                catchError((err: unknown) => {
                    if (err instanceof HttpErrorResponse && err.status === 403) {
                        return throwError(() => new SissCloudInvalidTokenError());
                    }
                    return throwError(() => err);
                }),
            ),
        );
        const [uploaded, notUploaded] = partition(
            signResults.map((result, idx) => [result, idx] as const),
            ([result]) => result.uploaded,
        );

        // Se l'upload è fallito per tutti, mostra il primo errore, poiché è probabile che tutti gli errori siano
        //   uguali (es. sessione scaduta).
        if (isEmpty(uploaded)) {
            const error =
                notUploaded.find(([res]) => res.serverError)?.[0].serverError ?? 'SissCloudErrorUploadingDocuments';
            throw new Error(this.translate.instant(error));
        }

        if (!isEmpty(notUploaded)) {
            let message = this.translate.instant('SissCloudErrorUploadingDocuments', { p0: notUploaded.length });
            if (!isEmpty(uploaded)) {
                message +=
                    '\n' + this.translate.instant('SissCloudErrorUploadingDocumentsContinue', { p0: uploaded.length });
            }
            await firstValueFrom(this.modalDialogNotificationService.warning(message));
        }

        const signSessionId = signResults.find((res) => res.signSessionId)?.signSessionId;
        if (!signSessionId) {
            throw new Error(this.translate.instant('SissCloudErrorUploadingDocuments'));
        }

        const uploadedReportIndexes = uploaded.map(([_, i]) => i);

        return { uploadedReportIndexes, signSessionId };
    }
}
